/**
 * Discoverability Prompt Engine
 *
 * Pure decision function for feature discovery prompts.
 *
 * Primary responsibilities:
 * - Select at most one relevant prompt
 * - Prefer the first-14/30 journey trigger when intent is weak
 * - Enforce cooldowns, dismissal limits, and session caps
 *
 * Dependencies:
 * - feature registry
 * - journey spine
 */

import type { BetaFeatureKey } from '@/lib/beta-features/registry'
import {
  DISCOVERABILITY_FEATURE_KEYS,
  getDiscoverabilityFeature,
  isDiscoverabilityFeatureAvailable,
  type DiscoverabilityFeatureKey,
  type DiscoverabilityPlanGate,
  type DiscoverabilityPromptKind,
  type DiscoverabilityTrackedFeatureKey,
  type DiscoverabilitySurface
} from './features'
import {
  DISCOVERABILITY_JOURNEY_STRETCH_DAYS,
  getDiscoverabilityJourneyProgress
} from './journey'

export const DISCOVERABILITY_MAX_SESSION_PROMPTS = 2
export const DISCOVERABILITY_PROMPT_COOLDOWN_DAYS = 7
/**
 * After dismiss / Later, wait this long before the same prompt can return.
 * Caps re-nags at about every 1–2 weeks (not permanent, not daily).
 */
export const DISCOVERABILITY_DISMISS_SNOOZE_DAYS = 14
/** @deprecated Prefer time-based {@link DISCOVERABILITY_DISMISS_SNOOZE_DAYS}. */
export const DISCOVERABILITY_MAX_DISMISSALS = 1

/** Chat footers only pitch when the last user turn matches intent — no journey spam. */
const INTENT_ONLY_SURFACES: ReadonlySet<DiscoverabilitySurface> = new Set([
  'assistant_response'
])

export type DiscoverabilityUsageSnapshot = {
  featureKey: DiscoverabilityTrackedFeatureKey
  firstUsedAt: string | null
  lastUsedAt: string | null
  useCount: number
  dismissedPromptCount: number
  completedOnboardingAt: string | null
  lastPromptedAt: string | null
}

export type DiscoverabilityIntentSignals = {
  phrases?: string[]
}

export type DiscoverabilityPromptCandidate = {
  featureKey: DiscoverabilityFeatureKey
  kind: DiscoverabilityPromptKind
  minimumPlan: DiscoverabilityPlanGate
  title: string
  body: string
  ctaLabel: string
  ctaHref: string
  surface: DiscoverabilitySurface
  trigger: string
}

export type DiscoverabilityPromptInput = {
  surface: DiscoverabilitySurface
  featureKeys?: DiscoverabilityFeatureKey[]
  route?: string | null
  /** Analytics override for the winning candidate's `trigger` (not a filter). */
  trigger?: string | null
  userPlan?: DiscoverabilityPlanGate | string | null
  /** Beta access map — required betas stay hidden unless `=== true` (fail closed). */
  enabledBetaFeatures?: Partial<Record<BetaFeatureKey, boolean>> | null
  usage: Partial<Record<DiscoverabilityFeatureKey, DiscoverabilityUsageSnapshot>>
  intentSignals?: DiscoverabilityIntentSignals
  /** Days since account creation — drives the single Losi-max journey trigger. */
  accountAgeDays?: number | null
  sessionPromptCount?: number
  hasActivePrompt?: boolean
  isTyping?: boolean
  isLoading?: boolean
  hasError?: boolean
  isSensitiveAction?: boolean
  /** Sidebar tips: skip the journey-card session budget. */
  ignoreSessionCap?: boolean
}

const millisecondsPerDay = 24 * 60 * 60 * 1000
const planRanks: Record<DiscoverabilityPlanGate, number> = {
  free: 0,
  plus: 1,
  pro: 2,
  ultra: 3,
  business: 4
}

const normalizePhrase = (phrase: string) => phrase.toLowerCase().trim()

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Whole-phrase boundaries for every needle (avoids "resume the thread" → files). */
const matchesIntentPhrase = (candidate: string, needle: string) => {
  const normalizedNeedle = normalizePhrase(needle)
  if (!normalizedNeedle) return false
  const pattern = new RegExp(
    `(^|[^a-z0-9_])${escapeRegExp(normalizedNeedle)}($|[^a-z0-9_])`,
    'i'
  )
  return pattern.test(candidate)
}

const normalizePlanGate = (plan: DiscoverabilityPromptInput['userPlan']) => {
  const value = String(plan || 'free').toLowerCase()

  if (value.startsWith('business') || value === 'enterprise') return 'business'
  if (value.startsWith('ultra') || value.startsWith('studio')) return 'ultra'
  if (value.startsWith('pro') || value.startsWith('byok')) return 'pro'
  if (value.startsWith('plus')) return 'plus'

  return 'free'
}

const canUseFeaturePlan = (
  userPlan: DiscoverabilityPromptInput['userPlan'],
  minimumPlan: DiscoverabilityPlanGate
) => {
  return planRanks[normalizePlanGate(userPlan)] >= planRanks[minimumPlan]
}

export const canUseDiscoverabilityFeature = canUseFeaturePlan

const wasTimestampWithinDays = (
  timestamp: string | null | undefined,
  days: number
) => {
  if (!timestamp) return false

  const at = new Date(timestamp).getTime()
  if (!Number.isFinite(at)) return false

  return Date.now() - at < days * millisecondsPerDay
}

const wasPromptedRecently = (lastPromptedAt: string | null | undefined) => {
  return wasTimestampWithinDays(lastPromptedAt, DISCOVERABILITY_PROMPT_COOLDOWN_DAYS)
}

/**
 * True while a prior dismiss is still in the snooze window.
 * Uses lastPromptedAt (updated on dismiss) so the clock starts at "Later".
 */
export const isDiscoverabilityDismissSnoozed = (
  usage: Pick<DiscoverabilityUsageSnapshot, 'dismissedPromptCount' | 'lastPromptedAt'> | null | undefined
) => {
  if (!usage || (usage.dismissedPromptCount ?? 0) <= 0) return false
  // No timestamp (legacy rows) — treat as still snoozed until they get a fresh clock.
  if (!usage.lastPromptedAt) return true
  return wasTimestampWithinDays(usage.lastPromptedAt, DISCOVERABILITY_DISMISS_SNOOZE_DAYS)
}

/** Client/local dismiss keys (checklist, etc.) — same 14-day window. */
export const isDiscoverabilityLocalDismissActive = (
  dismissedAt: string | null | undefined,
  snoozeDays: number = DISCOVERABILITY_DISMISS_SNOOZE_DAYS
) => {
  return wasTimestampWithinDays(dismissedAt, snoozeDays)
}

const getIntentScore = (
  featureKey: DiscoverabilityFeatureKey,
  input: DiscoverabilityPromptInput
) => {
  const feature = getDiscoverabilityFeature(featureKey)
  const phrases = input.intentSignals?.phrases?.map(normalizePhrase) ?? []
  const phraseMatch = feature.intentPhrases.some((phrase) => {
    return phrases.some((candidate) => matchesIntentPhrase(candidate, phrase))
  })

  if (phraseMatch) return 3

  // Surface membership alone must not qualify — journey or real intent only.
  return 0
}

const getJourneyBoost = (
  featureKey: DiscoverabilityFeatureKey,
  input: DiscoverabilityPromptInput
) => {
  const accountAgeDays = input.accountAgeDays
  if (accountAgeDays == null || accountAgeDays < 0) return 0
  if (accountAgeDays > DISCOVERABILITY_JOURNEY_STRETCH_DAYS) return 0

  const journey = getDiscoverabilityJourneyProgress({
    accountAgeDays,
    usage: input.usage,
    userPlan: input.userPlan,
    enabledBetaFeatures: input.enabledBetaFeatures
  })

  if (!journey.inStretchWindow) return 0
  if (journey.nextFeatureKey === featureKey) return 4
  if (journey.unlockedUnusedKeys.includes(featureKey)) return 2

  return 0
}

/**
 * Dedicated product surfaces must not pitch themselves ("Open Planner" on Planner).
 * Empty chat / sidebar / assistant_response stay eligible for every unused feature.
 */
const SURFACE_SELF_FEATURE: Partial<
  Record<DiscoverabilitySurface, DiscoverabilityFeatureKey>
> = {
  spaces: 'spaces',
  planner: 'planner',
  files: 'files',
  search: 'search',
  voice: 'voice_memos',
  connectors: 'connectors',
  crew: 'crew'
}

const canShowPrompt = (
  featureKey: DiscoverabilityFeatureKey,
  input: DiscoverabilityPromptInput
) => {
  const feature = getDiscoverabilityFeature(featureKey)
  const usage = input.usage[featureKey]

  if (
    !isDiscoverabilityFeatureAvailable(featureKey, {
      userPlan: input.userPlan,
      enabledBetaFeatures: input.enabledBetaFeatures
    })
  ) {
    return false
  }
  // Registry surfaces were advisory before — enforce so sidebar-only CTAs
  // (Library / Integrations) never hitch a ride under chat replies.
  if (!feature.surfaces.includes(input.surface)) {
    return false
  }
  if (SURFACE_SELF_FEATURE[input.surface] === featureKey) {
    return false
  }
  // Already discovered / used / onboarded — never re-pitch.
  if (usage?.firstUsedAt || (usage?.useCount ?? 0) > 0) return false
  if (usage?.completedOnboardingAt) return false
  if (isDiscoverabilityDismissSnoozed(usage)) return false
  // Shown recently (not dismissed) — short cooldown; dismiss uses the longer snooze above.
  if ((usage?.dismissedPromptCount ?? 0) <= 0 && wasPromptedRecently(usage?.lastPromptedAt)) {
    return false
  }

  const intentScore = getIntentScore(featureKey, input)
  if (INTENT_ONLY_SURFACES.has(input.surface)) {
    return intentScore > 0
  }

  return intentScore > 0 || getJourneyBoost(featureKey, input) > 0
}

export function getDiscoverabilityPrompts(
  input: DiscoverabilityPromptInput
): DiscoverabilityPromptCandidate[] {
  if (input.hasActivePrompt) return []
  if (input.isTyping || input.isLoading || input.hasError || input.isSensitiveAction) return []
  if (
    !input.ignoreSessionCap &&
    (input.sessionPromptCount ?? 0) >= DISCOVERABILITY_MAX_SESSION_PROMPTS
  ) {
    return []
  }

  const featureKeys = input.featureKeys ?? DISCOVERABILITY_FEATURE_KEYS

  const candidates = featureKeys
    .filter((featureKey) => canShowPrompt(featureKey, input))
    .map((featureKey) => {
      const feature = getDiscoverabilityFeature(featureKey)
      const intentScore = getIntentScore(featureKey, input)
      const journeyBoost = getJourneyBoost(featureKey, input)
      const score = Math.max(intentScore, journeyBoost)

      let trigger = 'surface'
      if (journeyBoost >= intentScore && journeyBoost > 0) {
        trigger = journeyBoost >= 4 ? 'journey' : 'journey_pool'
      } else if (intentScore > 1) {
        trigger = 'intent'
      }
      if (typeof input.trigger === 'string' && input.trigger.trim()) {
        trigger = input.trigger.trim()
      }

      return {
        candidate: {
          featureKey,
          kind: feature.kind,
          minimumPlan: feature.minimumPlan,
          title: feature.prompt.title,
          body: feature.prompt.body,
          ctaLabel: feature.prompt.ctaLabel,
          ctaHref: feature.prompt.ctaHref,
          surface: input.surface,
          trigger
        } satisfies DiscoverabilityPromptCandidate,
        score
      }
    })
    .sort((left, right) => right.score - left.score)

  const best = candidates[0]?.candidate
  return best ? [best] : []
}
